import { apiSlice } from "./apiSlice";

export interface CurrentUser {
  // false — вход отключён на сервере: пользователь анонимный и имеет все права.
  ssoEnabled: boolean;
  id: string | null;
  name: string | null;
  email: string | null;
  globalRoles: string[];
  roles: string[];
  permissions: string[];
}

export interface RoleRef {
  id: string;
  name: string;
}

export interface AccessUser {
  id: string;
  email: string | null;
  name: string | null;
  isBlocked: boolean;
  globalRoles: string[];
  roles: RoleRef[];
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AccessRole {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
}

export interface RoleInput {
  name: string;
  description: string | null;
  permissions: string[];
}

export interface PermissionInfo {
  name: string;
  description: string;
}

export interface PendingGrant {
  id: string;
  email: string;
  roleId: string;
  roleName: string;
  grantedBy: string | null;
  grantedAt: string;
}

export interface GlobalRoleMapping {
  globalRole: string;
  roleId: string;
  roleName?: string;
}

export interface AuditEntry {
  id: number;
  at: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  target: string | null;
  details: string | null;
}

const jsonHeaders = { "Content-Type": "application/json" };

export const accessApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "Me",
      "AccessUsers",
      "AccessRoles",
      "PendingGrants",
      "GlobalRoleMappings",
      "AccessAudit",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMe: builder.query<CurrentUser, void>({
        query: () => "/auth/me",
        providesTags: ["Me"],
      }),

      // Пользователи
      getAccessUsers: builder.query<AccessUser[], void>({
        query: () => "/admin/users",
        providesTags: ["AccessUsers"],
      }),
      setUserRoles: builder.mutation<
        AccessUser,
        { userId: string; roleIds: string[] }
      >({
        query: ({ userId, roleIds }) => ({
          url: `/admin/users/${encodeURIComponent(userId)}/roles`,
          method: "PUT",
          headers: jsonHeaders,
          body: { roleIds },
        }),
        invalidatesTags: ["AccessUsers", "AccessAudit"],
      }),
      setUserBlocked: builder.mutation<
        void,
        { userId: string; blocked: boolean }
      >({
        query: ({ userId, blocked }) => ({
          url: `/admin/users/${encodeURIComponent(userId)}/${
            blocked ? "block" : "unblock"
          }`,
          method: "POST",
        }),
        invalidatesTags: ["AccessUsers", "AccessAudit"],
      }),

      // Назначения по email
      getPendingGrants: builder.query<PendingGrant[], void>({
        query: () => "/admin/pending-grants",
        providesTags: ["PendingGrants"],
      }),
      addPendingGrant: builder.mutation<
        PendingGrant,
        { email: string; roleId: string }
      >({
        query: (body) => ({
          url: "/admin/pending-grants",
          method: "POST",
          headers: jsonHeaders,
          body,
        }),
        invalidatesTags: ["PendingGrants", "AccessAudit"],
      }),
      removePendingGrant: builder.mutation<void, string>({
        query: (grantId) => ({
          url: `/admin/pending-grants/${grantId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["PendingGrants", "AccessAudit"],
      }),

      // Роли
      getPermissions: builder.query<PermissionInfo[], void>({
        query: () => "/admin/permissions",
      }),
      getAccessRoles: builder.query<AccessRole[], void>({
        query: () => "/admin/roles",
        providesTags: ["AccessRoles"],
      }),
      createRole: builder.mutation<AccessRole, RoleInput>({
        query: (body) => ({
          url: "/admin/roles",
          method: "POST",
          headers: jsonHeaders,
          body,
        }),
        invalidatesTags: ["AccessRoles", "AccessAudit"],
      }),
      updateRole: builder.mutation<AccessRole, RoleInput & { id: string }>({
        query: ({ id, ...body }) => ({
          url: `/admin/roles/${id}`,
          method: "PUT",
          headers: jsonHeaders,
          body,
        }),
        // Права роли меняют права её владельцев — в том числе текущего пользователя.
        invalidatesTags: [
          "AccessRoles",
          "AccessUsers",
          "GlobalRoleMappings",
          "PendingGrants",
          "AccessAudit",
          "Me",
        ],
      }),
      deleteRole: builder.mutation<void, string>({
        query: (roleId) => ({
          url: `/admin/roles/${roleId}`,
          method: "DELETE",
        }),
        invalidatesTags: [
          "AccessRoles",
          "AccessUsers",
          "GlobalRoleMappings",
          "PendingGrants",
          "AccessAudit",
          "Me",
        ],
      }),

      // Глобальные роли ComradeMajor
      getGlobalRoleMappings: builder.query<GlobalRoleMapping[], void>({
        query: () => "/admin/global-role-mappings",
        providesTags: ["GlobalRoleMappings"],
      }),
      setGlobalRoleMappings: builder.mutation<
        GlobalRoleMapping[],
        GlobalRoleMapping[]
      >({
        query: (mappings) => ({
          url: "/admin/global-role-mappings",
          method: "PUT",
          headers: jsonHeaders,
          body: mappings.map(({ globalRole, roleId }) => ({
            globalRole,
            roleId,
          })),
        }),
        invalidatesTags: ["GlobalRoleMappings", "AccessAudit", "Me"],
      }),

      getAccessAudit: builder.query<AuditEntry[], number | void>({
        query: (take) => `/admin/audit?take=${take ?? 500}`,
        providesTags: ["AccessAudit"],
      }),
    }),
  });

export const {
  useGetMeQuery,
  useGetAccessUsersQuery,
  useSetUserRolesMutation,
  useSetUserBlockedMutation,
  useGetPendingGrantsQuery,
  useAddPendingGrantMutation,
  useRemovePendingGrantMutation,
  useGetPermissionsQuery,
  useGetAccessRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetGlobalRoleMappingsQuery,
  useSetGlobalRoleMappingsMutation,
  useGetAccessAuditQuery,
} = accessApi;
